import { useEffect, useRef, useState } from 'react';

/**
 * Fetches a media resource as a blob URL and manages its lifecycle.
 *
 * Pass a **stable** fetcher (wrap with `useCallback`) that accepts an
 * `AbortSignal` and resolves to a blob-URL string or `null`.  The hook
 * revokes the previous URL whenever the fetcher identity changes and on
 * unmount, so callers don't leak object URLs.
 *
 * Returns `null` while the fetch is in flight or if the fetcher itself
 * is `null` (disabled).
 */
export function useBlobUrl(
  fetcher: ((signal: AbortSignal) => Promise<string | null>) | null,
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    if (!fetcher) return;

    const ac = new AbortController();

    fetcher(ac.signal)
      .then((u) => {
        if (ac.signal.aborted) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        urlRef.current = u;
        setUrl(u);
      })
      .catch(() => {
        // Errors are silently swallowed — the consumer sees `null`.
      });

    return () => {
      ac.abort();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [fetcher]);

  return url;
}
